import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { useViewMode } from '../hooks/useViewMode';
import { messagingAPI, settingsAPI, whatsappAPI } from '../services/api';
import { useRecipients, RecipientPicker, RecipientCount } from '../components/messaging/RecipientPicker';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Send, MessageCircle, Plus, Pencil, Trash2, X,
  Loader, FileText, Tv, Wifi, History, Search, RefreshCw, CheckCircle2, XCircle, BellRing, Save, Siren, Users, User,
  Image as ImageIcon, Megaphone, Download, ChevronLeft, Phone,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// True in App View / narrow screens; lets the tabs below switch to their phone layout.
const APP = createContext(false);

export const PLACEHOLDERS = ['{{name}}', '{{area}}', '{{customerId}}', '{{username}}', '{{mobile}}', '{{expiry}}', '{{amount}}'];
// Only filled when a template is sent through the agent app's Collection Pending flow
// (value1 = visit date/time, value2 = reason picked, value3 = outstanding balance) —
// they render blank if used in a general broadcast sent from this page. {{link}} is
// filled in only for Payment Receipt / Renewal sends (the customer-facing invoice link).
const CONTEXT_PLACEHOLDERS = ['{{value1}}', '{{value2}}', '{{value3}}', '{{link}}'];
// Invoice Share gets its own richer named placeholders instead of value1/2/3 — the
// original hardcoded message carried this many distinct fields (see MessageTemplate.js).
const INVOICE_SHARE_PLACEHOLDERS = [
  '{{company}}', '{{receiptNo}}', '{{planName}}', '{{period}}',
  '{{paidAmount}}', '{{paymentMode}}', '{{paymentDate}}', '{{balanceLine}}', '{{link}}',
];
// {{catalog}} is a pre-formatted CableTV/Broadband plan list (only plans flagged
// featuredOnWebsite, filtered to the connection type the lead picked) — computed
// server-side same as invoice_share's {{balanceLine}}, so it can't be a plain field.
// {{link}} here is the public pricing page, not an invoice link.
const LEAD_INTEREST_PLACEHOLDERS = ['{{name}}', '{{catalog}}', '{{link}}'];
// Filled client-side only (frontend/src/utils/messageTemplates.js), not by the server's
// fillPlaceholders — {{period}} (advance_link, e.g. "Jan 2027") has no server-side meaning
// at all and renders blank if this template is ever picked from the broadcast composer.
const PAYMENT_LINK_PLACEHOLDERS = ['{{name}}', '{{amount}}', '{{link}}'];
const ADVANCE_LINK_PLACEHOLDERS = ['{{name}}', '{{amount}}', '{{link}}', '{{period}}'];
const PORTAL_LOGIN_PLACEHOLDERS = ['{{link}}', '{{loginId}}'];

export const CATEGORY_LABELS = {
  network_down: 'Network Down', network_restored: 'Network Restored',
  maintenance: 'Maintenance', payment: 'Payment', renewal: 'Renewal (Bill Generated)',
  payment_receipt: 'Payment Receipt', invoice_share: 'Invoice Share',
  // Both auto-send off a complaint's lifecycle (routes/complaints.js) — they were missing
  // here, so their templates showed a raw category slug and couldn't be picked at all.
  complaint_registered: 'Complaint Registered', complaint_resolved: 'Complaint Resolved',
  // Fires when a field agent saves a lead with "Save Lead & Notify" (routes/agentApp.js
  // POST /leads) — no Customer record exists yet, so only {{name}} resolves from the
  // standard placeholder set above.
  lead_interest: 'Lead Interest (Agent)',
  // Renewal Dashboard's one-click actions — see routes/customers.js POST /:id/send-template.
  // 'plans' fills {{catalog}} with the same live, featuredOnWebsite plan list as
  // lead_interest above; 'payment_qr'/'payment_qr_2' each expect the admin's own
  // UPI/payment QR as this template's own image (upload it below) — the "2" variant
  // is a second, independent barcode (e.g. a second bank account), Broadband row only.
  call_no_answer: "Call Couldn't Connect", plans: 'Send Plans', payment_qr: 'Send QR', payment_qr_2: 'Send QR (2nd Barcode)',
  // Reconnection Leads page's "Send Plans" for inactive CableTV customers — same {{catalog}} as 'plans' (CableTV only).
  win_back: 'Come Back Offer (Reconnection)',
  // Leads inbox "Docs" button (routes/connectionRequests.js PUT /:id/send-docs) — split by
  // serviceType below (CableTV vs Broadband need different paperwork); a 'both' lead gets
  // both templates sent one after another.
  lead_documents: 'Lead Documents Needed',
  // "Share via WhatsApp" wa.me text on the admin Customer Detail page's Advance Payment
  // Link modal, and the agent app's Payment Link / Advance Link buttons — filled and
  // opened straight in the browser, no server round-trip (see frontend's
  // utils/messageTemplates.js), unlike every other category on this page.
  payment_link: 'Payment Link', advance_link: 'Advance Link',
  // Customer Detail → App Login "Share on WhatsApp" (routes/customers.js POST
  // /:id/send-portal-login). Always kept: the server re-creates it and blocks deleting the last one.
  portal_login: 'Portal Login Link',
  custom: 'Custom',
};
const CATEGORY_COLORS = {
  network_down: '#dc2626', network_restored: '#16a34a',
  maintenance: '#d97706', payment: 'var(--primary)', renewal: 'var(--primary)',
  payment_receipt: '#0891b2', invoice_share: '#7c3aed',
  complaint_registered: '#d97706', complaint_resolved: '#16a34a',
  lead_interest: '#0d9488',
  call_no_answer: '#ea580c', plans: '#0d9488', payment_qr: '#111827', payment_qr_2: '#c026d3',
  win_back: '#be185d',
  lead_documents: '#9333ea',
  payment_link: '#2563eb', advance_link: '#0e7490',
  portal_login: '#4f46e5',
  custom: '#6b7280',
};

// Lets a single category (typically Payment Receipt) carry separate CableTV vs
// Broadband wording — the auto-send picks the exact match if one exists, else 'both'.
export const SERVICE_TYPE_LABELS = { catv: 'CableTV', broadband: 'Broadband', both: 'Both Services' };

const BUTTON_TYPE_LABELS = { url: 'Link Button', call: 'Call Button', quickReply: 'Quick Reply' };
const BUTTON_VALUE_PLACEHOLDER = { url: 'https://example.com', call: '+91 94290 12345', quickReply: 'Yes, I\'m interested' };
const BUTTON_VALUE_LABEL = { url: 'URL', call: 'Phone Number', quickReply: 'Reply Text' };
const MAX_TEMPLATE_BUTTONS = 5;

// ─── Template editor modal ────────────────────────────────────────────────────
function TemplateModal({ template, onClose, onSaved }) {
  const isNew = !template?._id;
  const [form, setForm] = useState({
    name: template?.name || '',
    channel: 'whatsapp', // WhatsApp is the only channel in use
    category: template?.category || 'custom',
    serviceType: template?.serviceType || 'both',
    body: template?.body || '',
    imageUrl: template?.imageUrl || '',
    footer: template?.footer || '',
    buttons: template?.buttons || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const res = await messagingAPI.uploadTemplateImage(file);
      set('imageUrl', res.data?.data?.url || '');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Image upload failed');
    } finally { setUploadingImage(false); }
  };

  const addButton = () => {
    if (form.buttons.length >= MAX_TEMPLATE_BUTTONS) return toast.error(`At most ${MAX_TEMPLATE_BUTTONS} buttons`);
    set('buttons', [...form.buttons, { type: 'url', label: '', value: '' }]);
  };
  const updateButton = (i, patch) => set('buttons', form.buttons.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const removeButton = (i) => set('buttons', form.buttons.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) return toast.error('Name and message body are required');
    for (const b of form.buttons) {
      if (!b.label.trim() || !b.value.trim()) return toast.error('Every button needs a label and a value');
    }
    setSaving(true);
    try {
      if (isNew) await messagingAPI.createTemplate(form);
      else await messagingAPI.updateTemplate(template._id, form);
      toast.success(isNew ? 'Template created' : 'Template updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{isNew ? 'New Template' : 'Edit Template'}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Template Name <span className="required">*</span></label>
            <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Network Down Alert" />
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <select className="form-control" value={form.category} onChange={e => set('category', e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Applies To</label>
            <select className="form-control" value={form.serviceType} onChange={e => set('serviceType', e.target.value)}>
              {Object.entries(SERVICE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="form-hint">
              For Payment Receipt: pick CableTV or Broadband to give that service its own wording — it's used automatically the moment a matching payment is collected. Leave "Both Services" for a shared/general template.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message Body <span className="required">*</span></label>
            <textarea className="form-control" rows={5} value={form.body} onChange={e => set('body', e.target.value)}
              placeholder="Dear {{name}}, services in {{area}} are temporarily down…" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {PLACEHOLDERS.map(p => (
                <button key={p} type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + p)}
                  style={{ fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer' }}>
                  {p}
                </button>
              ))}
            </div>
            {['invoice_share', 'payment_receipt'].includes(form.category) ? (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10, marginBottom: 4 }}>
                  Filled from the payment being shared — company name, receipt no., plan, period, amount paid, payment mode/date, and a ready-made "Remaining Balance…" or "No outstanding balance…" line:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {INVOICE_SHARE_PLACEHOLDERS.map(p => (
                    <button key={p} type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + p)}
                      style={{ fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--cb-93c5fd)', background: 'var(--cb-eff6ff)', color: 'var(--primary)', cursor: 'pointer' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </>
            ) : ['payment_link', 'advance_link'].includes(form.category) ? (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10, marginBottom: 4 }}>
                  The "Share via WhatsApp" text on the Advance Payment Link modal (admin) and Payment Link / Advance Link buttons (agent app) — filled and opened straight in the browser, not sent through the gateway.{form.category === 'advance_link' ? ' {{period}} is the month the advance covers, e.g. "Jan 2027".' : ''}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(form.category === 'advance_link' ? ADVANCE_LINK_PLACEHOLDERS : PAYMENT_LINK_PLACEHOLDERS).map(p => (
                    <button key={p} type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + p)}
                      style={{ fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--cb-93c5fd)', background: 'var(--cb-eff6ff)', color: 'var(--primary)', cursor: 'pointer' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </>
            ) : form.category === 'portal_login' ? (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10, marginBottom: 4 }}>
                  Sent through WhatsApp by "Share on WhatsApp" on Customer Detail → App Login. {'{{link}}'} is the customer portal login page and {'{{loginId}}'} is the mobile number the customer logs in with. This template can be edited but not deleted.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {PORTAL_LOGIN_PLACEHOLDERS.map(p => (
                    <button key={p} type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + p)}
                      style={{ fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--cb-93c5fd)', background: 'var(--cb-eff6ff)', color: 'var(--primary)', cursor: 'pointer' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </>
            ) : form.category === 'lead_interest' ? (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10, marginBottom: 4 }}>
                  Sent when a field agent saves a lead with "Save Lead & Notify", or admin clicks "Send Plans" in the Leads inbox — no customer account exists yet, so only {'{{name}}'} resolves. Set <strong>Service Type</strong> above to CableTV or Broadband to word this for that service — a catv lead gets the CableTV template, everyone else (broadband, both, or no category set) gets the Broadband one. {'{{catalog}}'} is the plan list (CableTV/Broadband plans flagged "Featured on Website", filtered to what the lead is interested in) and {'{{link}}'} is the public pricing page:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {LEAD_INTEREST_PLACEHOLDERS.map(p => (
                    <button key={p} type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + p)}
                      style={{ fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--cb-93c5fd)', background: 'var(--cb-eff6ff)', color: 'var(--primary)', cursor: 'pointer' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </>
            ) : ['plans', 'win_back'].includes(form.category) ? (
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10 }}>
                {form.category === 'win_back' ? 'Sent by "Send Plans" on the Reconnection Leads page (inactive CableTV customers).' : 'Sent by "Send Plans" on the Renewal Dashboard.'} {'{{catalog}}'} is the live plan list (plans ticked "Featured on Website"){form.category === 'win_back' ? ', CableTV only' : ''}.
                <button type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + '{{catalog}}')}
                  style={{ marginLeft: 6, fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--cb-93c5fd)', background: 'var(--cb-eff6ff)', color: 'var(--primary)', cursor: 'pointer' }}>
                  {'{{catalog}}'}
                </button>
              </div>
            ) : form.category === 'lead_documents' ? (
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10 }}>
                Sent from the Leads inbox's "Docs" button — no customer account exists yet, so only {'{{name}}'} resolves. Set <strong>Service Type</strong> above to CableTV or Broadband to word the document list for that service — a catv lead gets the CableTV template, everyone else (broadband, both, or no category set) gets the Broadband one.
              </div>
            ) : (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 10, marginBottom: 4 }}>
                  Only filled by specific agent-app flows (blank in a general broadcast) — meaning depends on which one sends this template:
                  Collection Pending (visit date/time, reason, outstanding amount) · Renewal/Bill Generated (bill period, bill amount, {'{{link}}'} = invoice link) · Payment Receipt (amount paid, receipt number, {'{{link}}'} = invoice link):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CONTEXT_PLACEHOLDERS.map(p => (
                    <button key={p} type="button" onClick={() => set('body', form.body + (form.body.endsWith(' ') || !form.body ? '' : ' ') + p)}
                      style={{ fontSize: '.72rem', fontFamily: 'monospace', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--cb-93c5fd)', background: 'var(--cb-eff6ff)', color: 'var(--primary)', cursor: 'pointer' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Image (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                <ImageIcon size={13} style={{ marginRight: 5 }} />{uploadingImage ? 'Uploading…' : 'Choose Image'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingImage}
                  onChange={e => pickImage(e.target.files?.[0])} />
              </label>
              {form.imageUrl && (
                <div style={{ position: 'relative' }}>
                  <img src={form.imageUrl} alt="" style={{ height: 48, borderRadius: 6, border: '1px solid var(--border)' }} />
                  <button type="button" onClick={() => set('imageUrl', '')}
                    style={{ position: 'absolute', top: -8, right: -8, background: 'var(--cb-dc2626)', color: 'var(--c-ffffff)', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: '.7rem', lineHeight: 1 }}>
                    <X size={11} />
                  </button>
                </div>
              )}
            </div>
            <div className="form-hint">Sent as the message's header image — only takes effect on WhatsApp.</div>
          </div>

          <div className="form-group">
            <label className="form-label">Footer (optional)</label>
            <input className="form-control" value={form.footer} onChange={e => set('footer', e.target.value)}
              placeholder="— Laxmi Cable Vision" maxLength={60} />
          </div>

          <div className="form-group">
            <label className="form-label">Buttons (optional)</label>
            <div className="form-hint" style={{ marginBottom: 8 }}>
              Adds tappable buttons under the message — WhatsApp only. Sending this template still requires picking it as a broadcast in the Send tab.
            </div>
            {form.buttons.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <select className="form-control" style={{ maxWidth: 130 }} value={b.type} onChange={e => updateButton(i, { type: e.target.value, value: '' })}>
                  {Object.entries(BUTTON_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input className="form-control" style={{ maxWidth: 140 }} value={b.label} onChange={e => updateButton(i, { label: e.target.value })}
                  placeholder="Button label" maxLength={20} />
                <input className="form-control" value={b.value} onChange={e => updateButton(i, { value: e.target.value })}
                  placeholder={BUTTON_VALUE_PLACEHOLDER[b.type]} title={BUTTON_VALUE_LABEL[b.type]} />
                <button type="button" className="btn btn-danger btn-sm" style={{ padding: '4px 8px', flexShrink: 0 }} onClick={() => removeButton(i)}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            {form.buttons.length < MAX_TEMPLATE_BUTTONS && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={addButton}>
                <Plus size={13} style={{ marginRight: 5 }} />Add Button
              </button>
            )}
          </div>
        </div>
        <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || uploadingImage}>
            {saving ? 'Saving…' : (isNew ? 'Create Template' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Send tab ─────────────────────────────────────────────────────────────────
// WhatsApp-only broadcast. Audience is service-first: CableTV (all / by node)
// or Broadband (by ISP and/or PON port via fiber mapping), plus custom numbers.
function SendTab({ templates, seed }) {
  const isApp = useContext(APP);
  // `seed` (App View): a template or an earlier message the user chose to reuse.
  const [body, setBody] = useState(seed?.body || '');
  const [templateId, setTemplateId] = useState(seed?.templateId || '');
  // Carried along from the selected template — cleared the moment the admin edits the
  // message by hand (applyTemplate/setTemplateId('') below), same as templateId itself.
  const [tplExtras, setTplExtras] = useState(seed?.extras || { imageUrl: '', footer: '', buttons: [] });
  const rcp = useRecipients();
  const { sendAudience, recipientCount } = rcp;
  // Replying from a conversation: send to that one number (the "Custom Numbers" audience).
  useEffect(() => {
    if (seed?.mobile) { rcp.setAudType('numbers'); rcp.setNumbersText(seed.mobile); }
  }, []);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // queueStatus tracks the self-hosted gateway's daily allowance.
  const [queueStatus, setQueueStatus] = useState(null);

  useEffect(() => {
    whatsappAPI.getQueueStatus().then(r => setQueueStatus(r.data?.data || null)).catch(() => setQueueStatus(null));
  }, []);
  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = templates.find(t => t._id === id);
    if (tpl) {
      setBody(tpl.body);
      setTplExtras({ imageUrl: tpl.imageUrl || '', footer: tpl.footer || '', buttons: tpl.buttons || [] });
    }
  };

  const send = async () => {
    if (!body.trim()) return toast.error('Write a message or pick a template');
    if (!recipientCount) return toast.error('No recipients matched');
    const hasExtras = !!tplExtras.imageUrl || tplExtras.buttons.length > 0;
    const via = ' via the self-hosted gateway';
    if (!window.confirm(`Send this WhatsApp message${hasExtras ? ' (with image/buttons)' : ''} to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}${via}?`)) return;
    setSending(true); setResult(null);
    try {
      const res = await messagingAPI.send({
        channel: 'whatsapp', body, audience: sendAudience(),
        ...(hasExtras ? { imageUrl: tplExtras.imageUrl, footer: tplExtras.footer, buttons: tplExtras.buttons } : {}),
      });
      setResult(res.data?.data || null);
      toast.success(res.data?.message || 'Sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally { setSending(false); }
  };

  // CSV of Address + Mobile (91-prefixed) for the currently-selected recipients — for
  // uploading into a third-party WhatsApp broadcaster (Heyo) instead of sending through
  // our own gateway. Uses the same audience the Send button would use, so unticking rows
  // in a list-preview type narrows the export too.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    if (!recipientCount) return toast.error('No recipients matched');
    setExporting(true);
    try {
      const { data } = await messagingAPI.exportRecipients(sendAudience());
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `heyo-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  return (
    <div className="card"><div className="card-body">
      {/* WhatsApp-only note */}
      {!isApp && (
      <div style={{ background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: '.83rem', color: 'var(--c-166534)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <MessageCircle size={15} />
        Broadcasts go out via the <b>self-hosted WhatsApp API</b>.
      </div>
      )}

      <div className="form-group">
        <label className="form-label">Send Via</label>
        <div style={{ fontSize: '.78rem', color: queueStatus && recipientCount > (queueStatus.dailyLimit - queueStatus.sentToday) ? 'var(--c-991b1b)' : 'var(--text-muted)' }}>
          {queueStatus
            ? `Today's usage: ${queueStatus.sentToday} / ${queueStatus.dailyLimit} sends. ${recipientCount > (queueStatus.dailyLimit - queueStatus.sentToday) ? `This broadcast (${recipientCount}) would exceed the remaining allowance.` : `${queueStatus.dailyLimit - queueStatus.sentToday} remain today.`}`
            : 'Checking gateway status…'}
          {!isApp && ' Paced with a jittered delay between messages to reduce ban risk.'}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Template</label>
        <select className="form-control" value={templateId} onChange={e => applyTemplate(e.target.value)}>
          <option value="">— Write custom message —</option>
          {templates.filter(t => t.isActive !== false).map(t => (
            <option key={t._id} value={t._id}>
              {t.name} ({CATEGORY_LABELS[t.category] || t.category}{t.serviceType && t.serviceType !== 'both' ? ` · ${SERVICE_TYPE_LABELS[t.serviceType]}` : ''})
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Message <span className="required">*</span></label>
        <textarea className="form-control" rows={4} value={body} onChange={e => { setBody(e.target.value); setTemplateId(''); setTplExtras({ imageUrl: '', footer: '', buttons: [] }); }}
          placeholder="Dear {{name}}, internet services in {{area}} are temporarily down…" />
        <div className="form-hint">
          Placeholders {PLACEHOLDERS.join(', ')} are filled per customer automatically.
        </div>
        {tplExtras.imageUrl || tplExtras.buttons.length > 0 ? (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--cb-eff6ff)', border: '1px solid var(--cb-bfdbfe)', borderRadius: 8 }}>
            {tplExtras.imageUrl && <img src={tplExtras.imageUrl} alt="" style={{ height: 36, borderRadius: 4 }} />}
            <span style={{ fontSize: '.8rem', color: 'var(--c-1e40af)' }}>
              This template sends {tplExtras.imageUrl ? 'an image' : ''}{tplExtras.imageUrl && tplExtras.buttons.length > 0 ? ' + ' : ''}
              {tplExtras.buttons.length > 0 ? `${tplExtras.buttons.length} button${tplExtras.buttons.length !== 1 ? 's' : ''}` : ''} on WhatsApp.
            </span>
          </div>
        ) : null}
      </div>

      <RecipientPicker r={rcp} />

      {/* Recipient preview + send */}
      <div className={isApp ? 'mc-sendbar' : undefined} style={isApp ? undefined : { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <RecipientCount r={rcp} />
        <button className="btn btn-secondary" style={{ marginLeft: 'auto' }} onClick={exportCsv} disabled={exporting || !recipientCount}
          title="Download Address + Mobile (91-prefixed) as CSV — for Heyo or another bulk WhatsApp tool">
          {exporting
            ? <><Loader size={14} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />Exporting…</>
            : <><Download size={14} style={{ marginRight: 6 }} />{isApp ? 'CSV' : 'Export CSV (Heyo)'}</>}
        </button>
        <button className="btn btn-primary" onClick={send} disabled={sending || !recipientCount}>
          {sending
            ? <><Loader size={14} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />Sending… (do not close)</>
            : <><MessageCircle size={14} style={{ marginRight: 6 }} />Send WhatsApp to {recipientCount}</>}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, background: result.failed ? 'var(--cb-fffbeb)' : 'var(--cb-f0fdf4)', border: `1px solid ${result.failed ? 'var(--cb-fde68a)' : 'var(--cb-bbf7d0)'}`, fontSize: '.85rem' }}>
          <b>Result:</b> {result.sent} sent, {result.failed} failed (of {result.total})
          {result.errors?.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', color: 'var(--c-92400e)' }}>
              {result.errors.map((e, i) => <li key={i}>{e.mobile} ({e.channel}): {e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </div></div>
  );
}

// ─── Push Offers tab ──────────────────────────────────────────────────────────
// Marketing push notifications (title + subtext + optional image) to whichever
// customers have opted in via the portal's Settings → Push Notifications toggle.
// Only reaches subscribed customers — the counts below already reflect that, unlike
// SendTab's WhatsApp preview which counts everyone with a mobile number on file.
function PushOffersTab() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audType, setAudType] = useState('all'); // all | catv | broadband
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const audience = useCallback(() => ({ type: audType }), [audType]);

  useEffect(() => {
    const t = setTimeout(() => {
      messagingAPI.previewPushOffer(audience())
        .then(r => setPreview(r.data?.data || null))
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(t);
  }, [audience]);

  const pickImage = (file) => {
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const send = async () => {
    if (!title.trim()) return toast.error('Write a title');
    if (!body.trim()) return toast.error('Write the offer subtext');
    if (!preview?.count) return toast.error('No subscribed customers matched');
    if (!window.confirm(`Send this push notification to ${preview.count} customer${preview.count !== 1 ? 's' : ''}?`)) return;

    setSending(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('body', body);
      if (url.trim()) fd.append('url', url.trim());
      fd.append('audience', JSON.stringify(audience()));
      if (imageFile) fd.append('image', imageFile);

      const res = await messagingAPI.sendPushOffer(fd);
      setResult(res.data?.data || null);
      toast.success(res.data?.message || 'Sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="card"><div className="card-body">
      <div style={{ background: 'var(--cb-eff6ff)', border: '1px solid var(--cb-bfdbfe)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: '.83rem', color: 'var(--c-1e40af)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Megaphone size={15} />
        Only reaches customers who enabled <b>Push Notifications</b> in their portal Settings — not every customer.
      </div>

      <div className="form-group">
        <label className="form-label">Title <span className="required">*</span></label>
        <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="Diwali Offer!" maxLength={65} />
      </div>

      <div className="form-group">
        <label className="form-label">Subtext <span className="required">*</span></label>
        <textarea className="form-control" rows={3} value={body} onChange={e => setBody(e.target.value)}
          placeholder="Get 1 month free on annual broadband renewal — this week only." />
      </div>

      <div className="form-group">
        <label className="form-label">Image (optional)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            <ImageIcon size={13} style={{ marginRight: 5 }} />Choose Image
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => pickImage(e.target.files?.[0])} />
          </label>
          {imagePreview && (
            <div style={{ position: 'relative' }}>
              <img src={imagePreview} alt="" style={{ height: 48, borderRadius: 6, border: '1px solid var(--border)' }} />
              <button type="button" onClick={() => pickImage(null)}
                style={{ position: 'absolute', top: -8, right: -8, background: 'var(--cb-dc2626)', color: 'var(--c-ffffff)', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: '.7rem', lineHeight: 1 }}>
                <X size={11} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Link when tapped (optional)</label>
        <input className="form-control" value={url} onChange={e => setUrl(e.target.value)} placeholder="/portal/plans (defaults to the portal home)" />
      </div>

      <div className="form-group">
        <label className="form-label">Recipients</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All Customers', icon: Users },
            { key: 'catv', label: 'CableTV', icon: Tv },
            { key: 'broadband', label: 'Broadband', icon: Wifi },
          ].map(a => (
            <button key={a.key} type="button" onClick={() => setAudType(a.key)}
              className={`btn btn-sm ${audType === a.key ? 'btn-primary' : 'btn-secondary'}`}>
              <a.icon size={13} style={{ marginRight: 5 }} />{a.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <div style={{ fontSize: '.86rem' }}>
          {preview
            ? <><b>{preview.count}</b> subscribed customer{preview.count !== 1 ? 's' : ''} will get this{preview.devices > preview.count ? ` (${preview.devices} devices)` : ''}</>
            : <span style={{ color: 'var(--text-muted)' }}>Loading recipient count…</span>}
        </div>
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={send} disabled={sending || !preview?.count}>
          {sending
            ? <><Loader size={14} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />Sending…</>
            : <><Megaphone size={14} style={{ marginRight: 6 }} />Send Push to {preview?.count || 0}</>}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, background: result.failedCustomers ? 'var(--cb-fffbeb)' : 'var(--cb-f0fdf4)', border: `1px solid ${result.failedCustomers ? 'var(--cb-fde68a)' : 'var(--cb-bbf7d0)'}`, fontSize: '.85rem' }}>
          <b>Result:</b> {result.sentCustomers} sent, {result.failedCustomers} failed (of {result.totalCustomers} customers, {result.devices} devices)
        </div>
      )}
    </div></div>
  );
}

// ─── Templates tab ────────────────────────────────────────────────────────────
function TemplatesTab({ templates, reload, isAdmin, onUse }) {
  const [modal, setModal] = useState(null); // null | {} | template
  // Renewal reminder + STB update notice wording lives in BillingSettings, not the
  // MessageTemplate collection — loaded/saved independently here (same
  // settingsAPI.getBilling/saveBilling pair the Alerts tab already keeps its own copy of)
  // so every piece of customer-facing wording is editable from this one tab.
  const [billing, setBilling] = useState(null);
  const [billingSaving, setBillingSaving] = useState(false);
  const isApp = useContext(APP);
  const [tq, setTq] = useState('');
  const [tcat, setTcat] = useState('all');
  const [tOpen, setTOpen] = useState(null);
  const shown = !isApp ? templates : templates.filter(t =>
    (tcat === 'all' || t.category === tcat)
    && (!tq || `${t.name} ${t.body}`.toLowerCase().includes(tq.toLowerCase())));
  const usedCats = [...new Set(templates.map(t => t.category))];

  useEffect(() => {
    settingsAPI.getBilling().then(r => setBilling(r.data?.data)).catch(() => {});
  }, []);

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      const r = await settingsAPI.saveBilling(billing);
      setBilling(r.data?.data);
      toast.success('Message wording saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setBillingSaving(false); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await messagingAPI.deleteTemplate(tpl._id);
      toast.success('Template deleted');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <>
    <div className="card"><div className="card-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 10 }}>
        {isApp ? (
          <div className="search-input-wrapper" style={{ flex: 1, minWidth: 0 }}>
            <Search size={14} className="search-icon" />
            <input className="search-input" placeholder="Search templates…" value={tq} onChange={e => setTq(e.target.value)} />
          </div>
        ) : (
          <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>
            Reusable message templates — placeholders are filled per customer at send time
          </span>
        )}
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>
            <Plus size={14} style={{ marginRight: isApp ? 0 : 5 }} />{isApp ? <span style={{ marginLeft: 4 }}>New</span> : 'New Template'}
          </button>
        )}
      </div>

      {isApp && usedCats.length > 1 && (
        <div className="mc-chips" style={{ marginBottom: 12 }}>
          {['all', ...usedCats].map(c => (
            <button key={c} type="button" onClick={() => setTcat(c)} className={`mc-chip${tcat === c ? ' on' : ''}`}>{c === 'all' ? 'All' : (CATEGORY_LABELS[c] || c)}</button>
          ))}
        </div>
      )}

      {shown.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>{templates.length === 0 ? 'No templates yet' : 'No matches'}</div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {shown.map(tpl => (
          <div key={tpl._id} className={isApp ? 'mc-tpl' : undefined} onClick={isApp ? () => setTOpen(tOpen === tpl._id ? null : tpl._id) : undefined}
            style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start', ...(isApp ? { borderRadius: 12, cursor: 'pointer', flexWrap: 'wrap' } : {}) }}>
            <FileText size={16} style={{ color: CATEGORY_COLORS[tpl.category] || 'var(--c-6b7280)', marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <b style={{ fontSize: '.88rem' }}>{tpl.name}</b>
                <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '1px 8px', borderRadius: 8, background: `${CATEGORY_COLORS[tpl.category] || '#6b7280'}18`, color: CATEGORY_COLORS[tpl.category] || 'var(--c-6b7280)' }}>
                  {CATEGORY_LABELS[tpl.category] || tpl.category}
                </span>
                {tpl.serviceType && tpl.serviceType !== 'both' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.68rem', fontWeight: 700, padding: '1px 8px', borderRadius: 8, background: 'var(--primary-light)', color: 'var(--primary)' }}>
                    {tpl.serviceType === 'catv' ? <Tv size={11} /> : <Wifi size={11} />}
                    {SERVICE_TYPE_LABELS[tpl.serviceType]}
                  </span>
                )}
                <span style={{ fontSize: '.68rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>WhatsApp</span>
                {tpl.imageUrl && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.68rem', fontWeight: 700, padding: '1px 8px', borderRadius: 8, background: 'var(--cb-f0fdf4)', color: 'var(--c-16a34a)' }}>
                    <ImageIcon size={11} />Image
                  </span>
                )}
                {tpl.buttons?.length > 0 && (
                  <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '1px 8px', borderRadius: 8, background: 'var(--cb-eff6ff)', color: 'var(--c-2563eb)' }}>
                    {tpl.buttons.length} button{tpl.buttons.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4, whiteSpace: 'pre-wrap', ...(isApp && tOpen !== tpl._id ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}) }}>{tpl.body}</div>
            </div>
            {isApp && tOpen === tpl._id && onUse && (
              <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center', minHeight: 40 }}
                onClick={e => { e.stopPropagation(); onUse(tpl); }}><Send size={13} style={{ marginRight: 6 }} />Send this template</button>
            )}
            {isAdmin && (!isApp || tOpen === tpl._id) && (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, ...(isApp ? { width: '100%' } : {}) }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-secondary btn-sm" style={isApp ? { flex: 1, justifyContent: 'center' } : { padding: '3px 8px' }} onClick={() => setModal(tpl)}><Pencil size={12} />{isApp && <span style={{ marginLeft: 6 }}>Edit</span>}</button>
                <button className="btn btn-danger btn-sm" style={isApp ? { flex: 1, justifyContent: 'center' } : { padding: '3px 8px' }} onClick={() => remove(tpl)}><Trash2 size={12} />{isApp && <span style={{ marginLeft: 6 }}>Delete</span>}</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {modal !== null && (
        <TemplateModal template={modal._id ? modal : null} onClose={() => setModal(null)} onSaved={reload} />
      )}
    </div></div>

    {billing && (
      <div className="card" style={{ marginTop: 16 }}><div className="card-body">
        <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: 16 }}>
          Renewal reminders and the CATV package-update notice — schedule (days before/after,
          cutoff time, on/off) is set in the <b>Customer Notify</b> and <b>Alerts</b> tabs; the
          wording is here.
        </p>

        <RenewalTemplateEditor
          templates={billing.whatsappTemplates || []}
          setTemplates={next => setBilling(b => ({ ...b, whatsappTemplates: next }))}
          isAdmin={isAdmin}
        />

        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Pre-Expiry Reminder
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Tv size={13} />CableTV</label>
            <textarea
              className="form-control" rows={3}
              value={billing.autoReminders?.preExpiry?.catvTemplate || ''}
              onChange={e => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), preExpiry: { ...(b.autoReminders?.preExpiry || {}), catvTemplate: e.target.value } } }))}
              disabled={!isAdmin}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wifi size={13} />Broadband</label>
            <textarea
              className="form-control" rows={3}
              value={billing.autoReminders?.preExpiry?.broadbandTemplate || ''}
              onChange={e => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), preExpiry: { ...(b.autoReminders?.preExpiry || {}), broadbandTemplate: e.target.value } } }))}
              disabled={!isAdmin}
            />
          </div>
          <div className="form-hint">
            Use {RENEWAL_TPL_PLACEHOLDERS.map(p => (
              <code key={p} style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{p}</code>
            ))} as placeholders. A customer due on both services gets both messages. Schedule (days before, on/off) is set in the Alerts tab.
          </div>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Post-Expiry Reminder
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Tv size={13} />CableTV</label>
            <textarea
              className="form-control" rows={3}
              value={billing.autoReminders?.postExpiry?.catvTemplate || ''}
              onChange={e => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), postExpiry: { ...(b.autoReminders?.postExpiry || {}), catvTemplate: e.target.value } } }))}
              disabled={!isAdmin}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wifi size={13} />Broadband</label>
            <textarea
              className="form-control" rows={3}
              value={billing.autoReminders?.postExpiry?.broadbandTemplate || ''}
              onChange={e => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), postExpiry: { ...(b.autoReminders?.postExpiry || {}), broadbandTemplate: e.target.value } } }))}
              disabled={!isAdmin}
            />
          </div>
          <div className="form-hint">
            Use {NOTIFY_PLACEHOLDERS.map(p => (
              <code key={p} style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{p}</code>
            ))} as placeholders. A customer overdue on both services gets both messages.
          </div>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
            STB Package Update Notice
          </div>
          <textarea
            className="form-control" rows={3}
            value={billing.autoReminders?.stbUpdateNotice?.template || ''}
            onChange={e => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), stbUpdateNotice: { ...(b.autoReminders?.stbUpdateNotice || {}), template: e.target.value } } }))}
            disabled={!isAdmin}
          />
          <div className="form-hint">
            Use {STB_NOTICE_PLACEHOLDERS.map(p => (
              <code key={p} style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{p}</code>
            ))} as placeholders — {'{{time}}'} fills in from the cutoff time set in the Alerts tab.
          </div>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginTop: 16 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Next Month Bill (Pending Dues) Notice
          </div>
          <textarea
            className="form-control" rows={3}
            value={billing.autoReminders?.nextMonthPendingBill?.template || ''}
            onChange={e => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), nextMonthPendingBill: { ...(b.autoReminders?.nextMonthPendingBill || {}), template: e.target.value } } }))}
            disabled={!isAdmin}
          />
          <div className="form-hint">
            Use {NEXT_MONTH_PENDING_PLACEHOLDERS.map(p => (
              <code key={p} style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{p}</code>
            ))} as placeholders. On/off is set in the Alerts tab.
          </div>
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={saveBilling} disabled={billingSaving}>
              <Save size={13} style={{ marginRight: 5 }} />
              {billingSaving ? 'Saving…' : 'Save Message Wording'}
            </button>
          </div>
        )}
      </div></div>
    )}

    <AlertTemplatesEditor isAdmin={isAdmin} />
    </>
  );
}

// Every operational WhatsApp alert's actual wording (ONU signal loss, PON port down,
// new complaint, the transaction feed, ...) — who receives each is a separate concern
// set in the Alerts tab. This used to be ~18 hardcoded template literals scattered across
// alarmService/paymentService/cronJobs/complaints/customers — see backend
// models/AlertSettings.js's ALERT_TEMPLATES for the catalogue every one of these now reads
// from, and services/alertService.js's notifyWithTemplate for how a saved override here
// actually takes effect. Collapsed by default — 18 entries would otherwise dominate the tab.
function AlertTemplatesEditor({ isAdmin }) {
  const [items, setItems] = useState(null); // null = loading
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    messagingAPI.getAlertTemplates().then(r => setItems(r.data?.data || [])).catch(() => {});
  }, []);

  const update = (key, body) => setItems(list => list.map(t => t.key === key ? { ...t, body } : t));
  const reset = (key) => setItems(list => list.map(t => t.key === key ? { ...t, body: t.defaultBody } : t));

  const save = async () => {
    setSaving(true);
    try {
      await messagingAPI.saveAlertTemplates(Object.fromEntries(items.map(t => [t.key, t.body])));
      toast.success('Alert message wording saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!items) return null;

  return (
    <div className="card" style={{ marginTop: 16 }}><div className="card-body">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: '.92rem' }}>Staff Alert Message Wording</div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
            The WhatsApp text for every operational alert — network faults, complaints, payments, recovery.
            Who receives each one is set in the <b>Alerts</b> tab.
          </div>
        </div>
        <span style={{ fontSize: '.78rem', color: 'var(--primary)', fontWeight: 600, flexShrink: 0, marginLeft: 12 }}>
          {open ? 'Hide ▲' : `Show all ${items.length} ▼`}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 16, display: 'grid', gap: 14 }}>
          {items.map(t => (
            <div key={t.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>
                  {t.label}
                </div>
                {isAdmin && t.body !== t.defaultBody && (
                  <button
                    type="button" className="btn btn-secondary btn-sm"
                    style={{ padding: '2px 8px', fontSize: '.72rem' }}
                    onClick={() => reset(t.key)}
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <textarea
                className="form-control" rows={4}
                value={t.body}
                onChange={e => update(t.key, e.target.value)}
                disabled={!isAdmin}
              />
              <div className="form-hint">
                Use {t.placeholders.map(p => (
                  <code key={p} style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{`{{${p}}}`}</code>
                ))} as placeholders.
              </div>
            </div>
          ))}

          {isAdmin && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                <Save size={13} style={{ marginRight: 5 }} />
                {saving ? 'Saving…' : 'Save Alert Wording'}
              </button>
            </div>
          )}
        </div>
      )}
    </div></div>
  );
}

// ─── History tab ──────────────────────────────────────────────────────────────
// Every message actually sent from anywhere in the app — cron reminders, invoice
// shares, agent app messages, renewal reminders, payment links, broadcasts —
// all funnel through services/messageService.sendToCustomer, which writes here.
const SOURCE_LABELS = {
  renewal_reminder_cron: 'Auto Reminder (daily)',
  renewal_reminder_manual: 'Renewal Reminder',
  invoice_share: 'Invoice Share',
  agent_message: 'Agent Message',
  agent_collection_pending: 'Collection Pending',
  agent_auto_notify: 'Agent Auto-Notify',
  broadcast: 'Broadcast',
  complaint_alert: 'Complaint (staff alert)',
  kapso_broadcast: 'Kapso Broadcast',
  kapso_send: 'Kapso',
};

function HistoryTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    messagingAPI.getHistory({ limit: 1000 })
      .then(r => setLogs(r.data?.data || []))
      .catch(() => toast.error('Failed to load message history'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = search.toLowerCase();
  const qd = search.replace(/\D/g, '');
  const filtered = logs.filter(l =>
    (!search
    || (l.customerName || '').toLowerCase().includes(q)
    || (qd.length > 0 && (l.mobile || '').replace(/\D/g, '').includes(qd)))
  );

  return (
    <div className="card"><div className="card-body">
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-input-wrapper" style={{ flex: 1, minWidth: 220 }}>
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search by name or mobile…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} style={{ marginRight: 5 }} />Refresh</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <History size={32} style={{ opacity: .3, marginBottom: 8 }} />
          <div>{logs.length === 0 ? 'No messages sent yet' : 'No matches'}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(l => (
            <div key={l._id} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
              border: '1px solid var(--border)', borderRadius: 8,
            }}>
              {l.status === 'sent'
                ? <CheckCircle2 size={16} color="var(--c-16a34a)" style={{ flexShrink: 0, marginTop: 2 }} />
                : <XCircle size={16} color="var(--c-dc2626)" style={{ flexShrink: 0, marginTop: 2 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <b style={{ fontSize: '.85rem' }}>{l.customerName || l.mobile || '—'}</b>
                  {l.mobile && <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>{l.mobile}</span>}
                  <span style={{
                    fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
                    padding: '1px 7px', borderRadius: 5, background: 'var(--bg)', color: 'var(--text-muted)',
                  }}>
                    {l.channel}
                  </span>
                  <span style={{ fontSize: '.72rem', color: 'var(--primary)' }}>{SOURCE_LABELS[l.source] || l.source}</span>
                  {/* Kapso only: filled in later by the Kapso webhook */}
                  {l.status === 'sent' && (l.deliveryStatus === 'delivered' || l.deliveryStatus === 'read') && (
                    <span style={{ fontSize: '.72rem', fontWeight: 600, color: l.deliveryStatus === 'read' ? 'var(--c-53bdeb)' : 'var(--text-muted)' }}>
                      ✓✓ {l.deliveryStatus === 'read' ? 'Read' : 'Delivered'}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: '.72rem', color: 'var(--text-muted)' }}>
                    {new Date(l.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                  {l.mediaUrls?.length ? l.body : (l.body?.length > 200 ? l.body.slice(0, 200) + '…' : l.body)}
                </div>
                {l.mediaUrls?.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {l.mediaUrls.map((m, i) => (m.type === 'video'
                      ? <video key={i} src={m.url} controls preload="metadata" style={{ height: 120, borderRadius: 6, border: '1px solid var(--border)' }} />
                      : <a key={i} href={m.url} target="_blank" rel="noreferrer"><img src={m.url} alt="" style={{ height: 120, borderRadius: 6, border: '1px solid var(--border)' }} /></a>))}
                  </div>
                )}
                {l.status === 'failed' && l.error && (
                  <div style={{ fontSize: '.74rem', color: 'var(--c-dc2626)', marginTop: 4 }}>Failed: {l.error}</div>
                )}
                {l.sentByName && (
                  <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 4 }}>by {l.sentByName}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div></div>
  );
}

// ─── Customer Notify (auto plan-expiry WhatsApp reminders) ────────────────────
const NOTIFY_PLACEHOLDERS = ['{{name}}', '{{expiry}}', '{{username}}', '{{daysExpired}}'];
const RENEWAL_TPL_PLACEHOLDERS = ['{{name}}', '{{expiry}}', '{{username}}'];
const STB_NOTICE_PLACEHOLDERS = ['{{name}}', '{{time}}'];
const NEXT_MONTH_PENDING_PLACEHOLDERS = ['{{name}}', '{{pendingAmount}}', '{{amount}}', '{{totalDue}}'];

// The renewal reminder templates (BillingSettings.whatsappTemplates). These used to live in
// Settings → Billing, which put them a page away from the pre-expiry reminder that is the
// only thing that reads them — they're edited here now, and still saved through the same
// billing-settings payload as the toggles below.
function RenewalTemplateEditor({ templates, setTemplates, isAdmin }) {
  const [editIdx, setEditIdx] = useState(null); // null | 'new' | number
  const [form, setForm] = useState({ name: '', body: '' });

  const cancel = () => { setEditIdx(null); setForm({ name: '', body: '' }); };
  const validate = () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return false; }
    if (!form.body.trim()) { toast.error('Template body is required'); return false; }
    return true;
  };

  const list = templates || [];

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: '.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
        Renewal Reminder Templates
      </div>
      <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        For manual sends elsewhere (Renewals, Message Center) — pick whichever template you like at send time.
        The automated before-expiry reminder uses its own CableTV/Broadband wording below instead. Use{' '}
        {RENEWAL_TPL_PLACEHOLDERS.map(p => (
          <code key={p} style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{p}</code>
        ))} as placeholders.
      </p>

      {list.map((tpl, idx) => (
        <div key={idx} style={{ border: editIdx === idx ? '1px solid var(--primary)' : '1px solid var(--border)', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
          {editIdx === idx ? (
            <div style={{ padding: 16, background: 'var(--cb-f0f9ff)' }}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>Edit Template</div>
              <div className="form-group">
                <label className="form-label">Template Name</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Short Reminder" style={{ maxWidth: 300 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Message Body</label>
                <textarea className="form-control" rows={4} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  if (!validate()) return;
                  setTemplates(list.map((t, i) => i === idx ? { ...t, name: form.name.trim(), body: form.body.trim() } : t));
                  cancel();
                }}><Save size={12} style={{ marginRight: 4 }} />Apply</button>
                <button className="btn btn-secondary btn-sm" onClick={cancel}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--bg)' }}>
                <div style={{ flex: 1, fontWeight: 600, fontSize: '.88rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {tpl.name}
                  {tpl.isDefault && (
                    <span style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-065f46)', background: 'var(--cb-d1fae5)', padding: '1px 6px', borderRadius: 3 }}>
                      Default
                    </span>
                  )}
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {!tpl.isDefault && (
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: '.75rem', padding: '2px 8px' }}
                        onClick={() => setTemplates(list.map((t, i) => ({ ...t, isDefault: i === idx })))}>
                        Set Default
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px' }}
                      onClick={() => { setEditIdx(idx); setForm({ name: tpl.name, body: tpl.body }); }}>
                      <Pencil size={12} />
                    </button>
                    <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px' }}
                      onClick={() => {
                        if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
                        const next = list.filter((_, i) => i !== idx);
                        // Never leave the list without a default — the pre-expiry reminder
                        // would silently stop finding a template to send.
                        if (tpl.isDefault && next.length > 0) next[0] = { ...next[0], isDefault: true };
                        setTemplates(next);
                      }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
              <div style={{ padding: '8px 14px 12px', fontSize: '.82rem', color: 'var(--text-muted)', lineHeight: 1.5, fontStyle: 'italic' }}>
                {tpl.body.length > 130 ? tpl.body.slice(0, 130) + '…' : tpl.body}
              </div>
            </>
          )}
        </div>
      ))}

      {!isAdmin ? null : editIdx === 'new' ? (
        <div style={{ border: '1px solid var(--primary)', borderRadius: 8, padding: 16, background: 'var(--cb-f0f9ff)' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>New Template</div>
          <div className="form-group">
            <label className="form-label">Template Name</label>
            <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Short Reminder" style={{ maxWidth: 300 }} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Message Body</label>
            <textarea className="form-control" rows={4} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={() => {
              if (!validate()) return;
              setTemplates([...list, { name: form.name.trim(), body: form.body.trim(), isDefault: list.length === 0 }]);
              cancel();
            }}><Save size={12} style={{ marginRight: 4 }} />Apply</button>
            <button className="btn btn-secondary btn-sm" onClick={cancel}>Cancel</button>
          </div>
        </div>
      ) : editIdx === null && (
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 4 }}
          onClick={() => { setEditIdx('new'); setForm({ name: '', body: '' }); }}>
          <Plus size={13} style={{ marginRight: 5 }} />Add Template
        </button>
      )}

      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 12 }}>
        Template changes are only stored once you hit <b>Save Message Wording</b> below.
      </div>
    </div>
  );
}

function CustomerNotifyTab({ isAdmin, goToTemplates }) {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    settingsAPI.getBilling()
      .then(r => setBilling(r.data?.data))
      .catch(() => toast.error('Failed to load Customer Notify settings'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await settingsAPI.saveBilling(billing);
      setBilling(r.data?.data);
      toast.success('Customer Notify settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !billing) {
    return <div className="card"><div className="card-body">
      <div style={{ textAlign: 'center', padding: 40 }}><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
    </div></div>;
  }

  const ar = billing.autoReminders || { enabled: true, preExpiry: { enabled: true, daysBefore: 3 }, postExpiry: { enabled: false, days: 10, template: '' } };
  const setAr = (patch) => setBilling(b => ({ ...b, autoReminders: { ...ar, ...patch } }));

  const oh = billing.officeHours || { enabled: true, start: '9:00 AM', end: '7:00 PM' };
  const setOh = (patch) => setBilling(b => ({ ...b, officeHours: { ...oh, ...patch } }));

  return (
    <div className="card"><div className="card-body">
      <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Automatically message customers over WhatsApp about their plan status — before it expires, and again
        every day for a while after, if they haven't renewed.
      </p>
      <button type="button" className="btn btn-secondary btn-sm" style={{ marginBottom: 18 }} onClick={goToTemplates}>
        <FileText size={13} style={{ marginRight: 6 }} />Edit the reminder wording in the Templates tab
      </button>

      <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', fontSize: '.95rem', marginBottom: 20, padding: '14px 16px', background: ar.enabled ? 'var(--cb-f0fdf4)' : 'var(--cb-f8fafc)', border: `1px solid ${ar.enabled ? 'var(--cb-bbf7d0)' : 'var(--border)'}`, borderRadius: 8 }}>
        <input
          type="checkbox"
          checked={!!ar.enabled}
          onChange={e => setAr({ enabled: e.target.checked })}
          disabled={!isAdmin}
          style={{ width: 18, height: 18, accentColor: 'var(--success)', cursor: 'pointer' }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>Automatically inform customers about their plans</div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {ar.enabled
              ? 'On — the reminders below will be sent automatically over WhatsApp.'
              : 'Off — neither reminder below will be sent, regardless of their own toggle.'}
          </div>
        </div>
      </label>

      <div style={{ opacity: ar.enabled ? 1 : 0.5, pointerEvents: ar.enabled ? 'auto' : 'none' }}>
        {/* Pre-expiry reminder */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600, marginBottom: ar.preExpiry?.enabled ? 12 : 0 }}>
            <input
              type="checkbox"
              checked={!!ar.preExpiry?.enabled}
              onChange={e => setAr({ preExpiry: { ...ar.preExpiry, enabled: e.target.checked } })}
              disabled={!isAdmin}
              style={{ width: 16, height: 16, accentColor: 'var(--success)', cursor: 'pointer' }}
            />
            Before expiry — separate CableTV/Broadband wording (Templates tab)
          </label>
          {ar.preExpiry?.enabled && (
            <div className="form-group">
              <label className="form-label">Send starting this many days before expiry</label>
              <input
                type="number" min={1} max={30} className="form-control" style={{ maxWidth: 120 }}
                value={ar.preExpiry?.daysBefore ?? 3}
                onChange={e => setAr({ preExpiry: { ...ar.preExpiry, daysBefore: parseInt(e.target.value) || 1 } })}
                disabled={!isAdmin}
              />
            </div>
          )}
        </div>

        {/* Portal login details on CableTV reminders */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={ar.catvPortalDetails !== false}
              onChange={e => setAr({ catvPortalDetails: e.target.checked })}
              disabled={!isAdmin}
              style={{ width: 16, height: 16, accentColor: 'var(--success)', cursor: 'pointer' }}
            />
            CableTV reminders — include customer portal link, login ID and password
          </label>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Added to the before- and after-expiry CableTV reminders. The password is shown only while it is still the default (last 4 digits of the mobile).
          </div>
        </div>

        {/* Post-expiry reminder */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600, marginBottom: ar.postExpiry?.enabled ? 12 : 0 }}>
            <input
              type="checkbox"
              checked={!!ar.postExpiry?.enabled}
              onChange={e => setAr({ postExpiry: { ...ar.postExpiry, enabled: e.target.checked } })}
              disabled={!isAdmin}
              style={{ width: 16, height: 16, accentColor: 'var(--success)', cursor: 'pointer' }}
            />
            After expiry — daily reminder that the plan has expired
          </label>
          {ar.postExpiry?.enabled && (
            <>
              <div className="form-group">
                <label className="form-label">Keep sending daily for this many days after expiry</label>
                <input
                  type="number" min={1} max={60} className="form-control" style={{ maxWidth: 120 }}
                  value={ar.postExpiry?.days ?? 10}
                  onChange={e => setAr({ postExpiry: { ...ar.postExpiry, days: parseInt(e.target.value) || 1 } })}
                  disabled={!isAdmin}
                />
                <div className="form-hint">After this many days, the customer stops receiving this reminder.</div>
              </div>
              <div className="form-hint">Message wording is edited in the Templates tab.</div>
            </>
          )}
        </div>
      </div>

      {/* Office Hours — informational only. Advance recharge (portal /portal/renew) never
          blocks a payment on this; it only adds a note to the success screen when the
          payment lands outside this window, since a CableTV channel/package change may
          only take effect once the office reopens. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginTop: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600, marginBottom: oh.enabled ? 12 : 0 }}>
          <input
            type="checkbox"
            checked={!!oh.enabled}
            onChange={e => setOh({ enabled: e.target.checked })}
            disabled={!isAdmin}
            style={{ width: 16, height: 16, accentColor: 'var(--success)', cursor: 'pointer' }}
          />
          Show an "outside office hours" note on advance recharge
        </label>
        {oh.enabled && (
          <>
            <div className="form-hint" style={{ marginBottom: 10 }}>
              Payments are always accepted, any time — this only adds a note to the confirmation screen when a
              customer recharges outside these hours.
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Opens at</label>
                <input className="form-control" placeholder="9:00 AM" value={oh.start || ''} onChange={e => setOh({ start: e.target.value })} disabled={!isAdmin} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Closes at</label>
                <input className="form-control" placeholder="7:00 PM" value={oh.end || ''} onChange={e => setOh({ end: e.target.value })} disabled={!isAdmin} />
              </div>
            </div>
          </>
        )}
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <Save size={14} style={{ marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save Customer Notify Settings'}
          </button>
        </div>
      )}
    </div></div>
  );
}

// ─── Alerts (who gets WhatsApped when something happens) ──────────────────────
// Staff-facing alerts, as opposed to everything else on this page which targets customers.
// The event catalogue comes from the server (models/AlertSettings.js) so this renders
// itself — a new alert type needs no change here.
const GROUP_ORDER = ['Money', 'Network', 'Support', 'Recovery'];

// When each scheduled message goes out (utils/messageSchedules.js) and the GTPL wallet-low
// alert's limit. Own save, independent of the recipients list below.
function SendingTimesCard({ isAdmin }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    messagingAPI.getSchedules().then(r => setData(r.data?.data)).catch(err => toast.error(err.response?.data?.message || 'Failed to load sending times'));
  }, []);
  if (!data) return null;
  const setTime = (key, time) => setData(d => ({ ...d, schedules: d.schedules.map(s => s.key === key ? { ...s, time } : s) }));
  const setWallet = (patch) => setData(d => ({ ...d, walletLow: { ...d.walletLow, ...patch } }));
  const save = async () => {
    setSaving(true);
    try {
      await messagingAPI.saveSchedules({
        schedules: Object.fromEntries(data.schedules.map(s => [s.key, s.time])),
        walletLow: { threshold: Number(data.walletLow.threshold) },
      });
      toast.success('Sending times saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 20, background: 'var(--cb-fafafa)' }}>
      <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
        Sending Times
      </div>
      <table className="table" style={{ marginBottom: 14 }}>
        <tbody>
          {data.schedules.map(s => (
            <tr key={s.key}>
              <td style={{ fontSize: '.85rem' }}>
                {s.label}
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                  {s.audience}{s.dayOfMonth ? ` · on the ${s.dayOfMonth}th of each month` : ' · every day'} · default {s.defaultTime}
                </div>
              </td>
              <td style={{ width: 140 }}>
                <input type="time" className="form-control" value={s.time} onChange={e => setTime(s.key, e.target.value)} disabled={!isAdmin} required />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: 6 }}>GTPL wallet low alert</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Alert when a wallet is below (₹)</label>
          <input type="number" min="1" className="form-control" style={{ maxWidth: 160 }} value={data.walletLow.threshold}
            onChange={e => setWallet({ threshold: e.target.value })} disabled={!isAdmin} />
        </div>
      </div>
      <div className="form-hint" style={{ marginBottom: 12 }}>
        Sent once a day at the "GTPL wallet low" time above, as one message listing every wallet below this limit — nothing is sent if they are all above it.
        A time moved later on a day the message already went out won't send it again that day.
      </div>
      {isAdmin && (
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Sending Times'}
        </button>
      )}
    </div>
  );
}

function AlertsTab({ isAdmin, goToTemplates }) {
  const [events, setEvents] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  // Customer Messages section below is the one exception on this page — everything else
  // here is a staff alert (AlertSettings), but the STB update notice actually messages the
  // CUSTOMER (see BillingSettings.autoReminders.stbUpdateNotice / cronJobs.js's stbNotice
  // job). Kept here anyway, at the user's request, so every automated-message toggle lives
  // on one screen — it's a separate save (settingsAPI.saveBilling), independent of the
  // recipients list above.
  const [billing, setBilling] = useState(null);
  const [billingSaving, setBillingSaving] = useState(false);
  // WhatsApp groups the connected gateway session can see — only fetched once a group
  // recipient is actually in use, since it requires a real call to the gateway and a lot of
  // installs will never touch this. null = not fetched yet, 'error' = fetch failed.
  const [waGroups, setWaGroups] = useState(null);
  const [waGroupsLoading, setWaGroupsLoading] = useState(false);

  const loadWaGroups = useCallback(() => {
    setWaGroupsLoading(true);
    messagingAPI.getAlertGroups()
      .then(r => setWaGroups(r.data?.data || []))
      .catch(err => {
        setWaGroups('error');
        toast.error(err.response?.data?.message || 'Could not load WhatsApp groups');
      })
      .finally(() => setWaGroupsLoading(false));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    messagingAPI.getAlertRecipients()
      .then(r => {
        const recs = (r.data?.data?.recipients || []).map(rec => ({ type: 'contact', ...rec }));
        setEvents(r.data?.data?.events || []);
        setRecipients(recs);
        if (recs.some(rec => rec.type === 'group')) loadWaGroups();
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load alert recipients'))
      .finally(() => setLoading(false));
  }, [loadWaGroups]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    settingsAPI.getBilling().then(r => setBilling(r.data?.data)).catch(() => {});
  }, []);

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      const r = await settingsAPI.saveBilling(billing);
      setBilling(r.data?.data);
      toast.success('Customer message settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setBillingSaving(false); }
  };

  const patch = (idx, changes) => setRecipients(rs => rs.map((r, i) => i === idx ? { ...r, ...changes } : r));
  const toggleEvent = (idx, key) => setRecipients(rs => rs.map((r, i) =>
    i === idx ? { ...r, events: { ...r.events, [key]: !r.events?.[key] } } : r));

  const addRecipient = () => setRecipients(rs => [...rs, { name: '', type: 'contact', mobile: '', groupId: '', isActive: true, events: {} }]);
  const setType = (idx, type) => {
    if (type === 'group' && waGroups === null && !waGroupsLoading) loadWaGroups();
    patch(idx, { type });
  };

  const save = async () => {
    for (const r of recipients) {
      if (r.type === 'group') {
        if (!String(r.groupId || '').trim()) { toast.error('Every group recipient needs a group selected'); return; }
      } else if (!String(r.mobile || '').trim()) {
        toast.error('Every recipient needs a mobile number');
        return;
      }
    }
    setSaving(true);
    try {
      await messagingAPI.saveAlertRecipients(recipients);
      toast.success('Alert recipients saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendTest = async (r) => {
    const dest = r.type === 'group' ? r.groupId : r.mobile;
    setTesting(dest);
    try {
      await messagingAPI.testAlertRecipient(r.type === 'group' ? { type: 'group', groupId: r.groupId } : { mobile: r.mobile });
      toast.success(r.type === 'group' ? 'Test alert sent to the group' : `Test alert sent to ${dest}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Test alert failed');
    } finally { setTesting(null); }
  };

  if (loading) {
    return <div className="card"><div className="card-body">
      <div style={{ textAlign: 'center', padding: 40 }}><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
    </div></div>;
  }

  // Events grouped for display, with anything in an unrecognised group falling in at the end
  // rather than disappearing.
  const groups = [...new Set([...GROUP_ORDER, ...events.map(e => e.group || 'Other')])]
    .map(g => ({ group: g, items: events.filter(e => (e.group || 'Other') === g) }))
    .filter(g => g.items.length);

  return (
    <div className="card"><div className="card-body">
      <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.5 }}>
        Add the mobile numbers that should be WhatsApped when something happens on the network or in the
        office, and tick exactly which alerts each number receives. These go to <b>your team</b>, never to
        customers — customer messaging is the other tabs on this page.
      </p>

      <SendingTimesCard isAdmin={isAdmin} />

      {/* Customer Messages — the toggles here actually message the CUSTOMER, not your
          team. Kept on this page for convenience; saved separately (Customer Notify's
          own BillingSettings), independent of the staff recipients below. */}
      {billing && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 20, background: 'var(--cb-fafafa)' }}>
          <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
            Customer Messages
          </div>
          {(() => {
            const ar = billing.autoReminders || {};
            const stb = ar.stbUpdateNotice || { enabled: false, cutoffTime: '1:00 PM', template: '' };
            const setStb = (patch) => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), stbUpdateNotice: { ...stb, ...patch } } }));
            const nmp = ar.nextMonthPendingBill || { enabled: false, template: '' };
            const setNmp = (patch) => setBilling(b => ({ ...b, autoReminders: { ...(b.autoReminders || {}), nextMonthPendingBill: { ...nmp, ...patch } } }));
            const mbg = billing.monthlyBillGeneration || { sendMessage: true };
            const setMbg = (patch) => setBilling(b => ({ ...b, monthlyBillGeneration: { ...mbg, ...patch } }));
            return (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600, marginBottom: stb.enabled ? 12 : 16 }}>
                  <input
                    type="checkbox"
                    checked={!!stb.enabled}
                    onChange={e => setStb({ enabled: e.target.checked })}
                    disabled={!isAdmin}
                    style={{ width: 16, height: 16, accentColor: 'var(--success)', cursor: 'pointer' }}
                  />
                  CATV package update today — sent to every customer whose new package starts today (time: Sending Times above)
                </label>
                {stb.enabled && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Ask customers to keep their Set-Top Box on until</label>
                      <input
                        className="form-control" style={{ maxWidth: 160 }}
                        value={stb.cutoffTime ?? '1:00 PM'}
                        onChange={e => setStb({ cutoffTime: e.target.value })}
                        disabled={!isAdmin}
                        placeholder="1:00 PM"
                      />
                    </div>
                    <div className="form-hint" style={{ marginBottom: 16 }}>Message wording is edited in the Templates tab.</div>
                  </>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!nmp.enabled}
                    onChange={e => setNmp({ enabled: e.target.checked })}
                    disabled={!isAdmin}
                    style={{ width: 16, height: 16, accentColor: 'var(--success)', cursor: 'pointer' }}
                  />
                  Auto-generate next month's bill for regular CATV customers still pending on this month —
                  runs on the 16th (time: Sending Times above)
                </label>
                {nmp.enabled && (
                  <>
                    <div style={{ display: 'flex', gap: 16, marginTop: 8, marginBottom: 6, fontSize: '.85rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input
                          type="radio" name="nmpSendMessage" checked={nmp.sendMessage !== false}
                          onChange={() => setNmp({ sendMessage: true })} disabled={!isAdmin}
                        />
                        Send message to customer
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input
                          type="radio" name="nmpSendMessage" checked={nmp.sendMessage === false}
                          onChange={() => setNmp({ sendMessage: false })} disabled={!isAdmin}
                        />
                        Don't send message
                      </label>
                    </div>
                    {nmp.sendMessage !== false && (
                      <div className="form-hint" style={{ marginBottom: 6 }}>Message wording is edited in the Templates tab.</div>
                    )}
                  </>
                )}

                <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 14 }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 600, marginBottom: 8 }}>
                    Monthly bill generation for regular CATV customers — runs on the 1st of every month
                  </div>
                  <div className="form-hint" style={{ marginBottom: 8 }}>
                    The bill is always generated either way. This only controls whether the customer
                    gets a WhatsApp message about it.
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: '.85rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="radio" name="mbgSendMessage" checked={mbg.sendMessage !== false}
                        onChange={() => setMbg({ sendMessage: true })} disabled={!isAdmin}
                      />
                      Send message to customer
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="radio" name="mbgSendMessage" checked={mbg.sendMessage === false}
                        onChange={() => setMbg({ sendMessage: false })} disabled={!isAdmin}
                      />
                      Don't send message
                    </label>
                  </div>
                </div>

                {isAdmin && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={goToTemplates}>
                      <FileText size={13} style={{ marginRight: 6 }} />Templates tab
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={saveBilling} disabled={billingSaving}>
                      <Save size={13} style={{ marginRight: 5 }} />
                      {billingSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {recipients.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 8, marginBottom: 14 }}>
          No alert recipients yet — nobody is being notified. Add a number below.
        </div>
      )}

      {recipients.map((r, idx) => {
        const tickedCount = Object.values(r.events || {}).filter(Boolean).length;
        return (
          <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14, overflow: 'hidden', opacity: r.isActive === false ? 0.6 : 1 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, padding: '12px 14px', background: 'var(--bg)' }}>
              <div className="form-group" style={{ margin: 0, flex: '1 1 180px' }}>
                <label className="form-label">Name / label</label>
                <input className="form-control" value={r.name || ''} disabled={!isAdmin}
                  placeholder="e.g. Office, Technician, Team Group"
                  onChange={e => patch(idx, { name: e.target.value })} />
              </div>

              <div className="form-group" style={{ margin: 0, flex: '0 0 auto' }}>
                <label className="form-label">Type</label>
                <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                  {[{ v: 'contact', label: 'Contact', Icon: User }, { v: 'group', label: 'Group', Icon: Users }].map(({ v, label, Icon }) => (
                    <button key={v} type="button" disabled={!isAdmin}
                      onClick={() => setType(idx, v)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '7px 11px', fontSize: '.82rem', fontWeight: 600,
                        border: 'none', cursor: isAdmin ? 'pointer' : 'default',
                        background: (r.type || 'contact') === v ? 'var(--primary)' : 'transparent',
                        color: (r.type || 'contact') === v ? 'var(--c-ffffff)' : 'var(--text-muted)',
                      }}>
                      <Icon size={13} />{label}
                    </button>
                  ))}
                </div>
              </div>

              {r.type === 'group' ? (
                <div className="form-group" style={{ margin: 0, flex: '1 1 220px' }}>
                  <label className="form-label">WhatsApp group</label>
                  {Array.isArray(waGroups) ? (
                    <select className="form-control" disabled={!isAdmin} value={r.groupId || ''}
                      onChange={e => {
                        const g = waGroups.find(g => g.id === e.target.value);
                        patch(idx, { groupId: e.target.value, name: r.name || g?.name || '' });
                      }}>
                      <option value="">
                        {r.groupId && !waGroups.some(g => g.id === r.groupId) ? `(saved: ${r.groupId})` : 'Select a group…'}
                      </option>
                      {waGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-control" value={r.groupId || ''} disabled={!isAdmin}
                        placeholder={waGroups === 'error' ? 'Paste group id (list failed to load)' : 'Loading groups…'}
                        onChange={e => patch(idx, { groupId: e.target.value })} />
                      {isAdmin && waGroups === 'error' && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={loadWaGroups} title="Retry loading groups">
                          <RefreshCw size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
                  <label className="form-label">Mobile number</label>
                  <input className="form-control" value={r.mobile || ''} disabled={!isAdmin}
                    placeholder="10-digit number"
                    onChange={e => patch(idx, { mobile: e.target.value })} />
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.85rem', fontWeight: 600, cursor: isAdmin ? 'pointer' : 'default', paddingBottom: 9 }}>
                <input type="checkbox" checked={r.isActive !== false} disabled={!isAdmin}
                  onChange={e => patch(idx, { isActive: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: 'var(--success)' }} />
                Active
              </label>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 6, paddingBottom: 6 }}>
                  <button className="btn btn-secondary btn-sm"
                    disabled={r.type === 'group' ? (!r.groupId || testing === r.groupId) : (!r.mobile || testing === r.mobile)}
                    onClick={() => sendTest(r)}>
                    {testing === (r.type === 'group' ? r.groupId : r.mobile)
                      ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />}
                    <span style={{ marginLeft: 5 }}>Test</span>
                  </button>
                  <button className="btn btn-danger btn-sm"
                    onClick={() => {
                      if (!window.confirm(`Remove ${r.name || r.mobile || 'this recipient'} from alert recipients?`)) return;
                      setRecipients(rs => rs.filter((_, i) => i !== idx));
                    }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>

            <div style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 10 }}>
                Receives {tickedCount === 0 ? 'nothing yet' : `${tickedCount} alert${tickedCount === 1 ? '' : 's'}`}
              </div>
              {groups.map(g => (
                <div key={g.group} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>{g.group}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                    {g.items.map(ev => {
                      const on = !!r.events?.[ev.key];
                      return (
                        <label key={ev.key}
                          style={{
                            display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 11px', borderRadius: 6,
                            border: `1px solid ${on ? 'var(--cb-bbf7d0)' : 'var(--border)'}`, background: on ? 'var(--cb-f0fdf4)' : 'transparent',
                            cursor: isAdmin ? 'pointer' : 'default',
                          }}>
                          <input type="checkbox" checked={on} disabled={!isAdmin}
                            onChange={() => toggleEvent(idx, ev.key)}
                            style={{ width: 16, height: 16, accentColor: 'var(--success)', marginTop: 2, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: '.85rem', fontWeight: 600 }}>{ev.label}</div>
                            <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', lineHeight: 1.45, marginTop: 2 }}>{ev.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={addRecipient}>
            <Plus size={13} style={{ marginRight: 5 }} />Add Number
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <Save size={14} style={{ marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save Alert Recipients'}
          </button>
        </div>
      )}
    </div></div>
  );
}

// ─── Messages (App View) ──────────────────────────────────────────────────────
// Phone version of the send log, laid out like a chat app: one row per customer with the
// latest message and its status; tap a row to read that customer's messages as a thread,
// with "Message" at the bottom to write to them. Same data as the History tab.
const convKey = (l) => (l.mobile || '').replace(/\D/g, '') || l.customerName || '—';
const dayLabel = (d) => {
  const x = new Date(d), t = new Date();
  if (x.toDateString() === t.toDateString()) return 'Today';
  if (x.toDateString() === new Date(t.getFullYear(), t.getMonth(), t.getDate() - 1).toDateString()) return 'Yesterday';
  return x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const shortTime = (d) => {
  const x = new Date(d);
  return x.toDateString() === new Date().toDateString()
    ? x.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};
// Same tick meaning as WhatsApp: ✓ sent, ✓✓ delivered, blue ✓✓ read, red ! failed.
function Ticks({ l }) {
  if (l.status !== 'sent') return <b style={{ color: 'var(--c-dc2626)' }}>Failed</b>;
  if (l.deliveryStatus === 'read') return <span style={{ color: 'var(--c-53bdeb)', fontWeight: 700 }}>✓✓</span>;
  if (l.deliveryStatus === 'delivered') return <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>✓✓</span>;
  return <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>✓</span>;
}

function MessagesApp({ thread, setThread, onCompose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [openId, setOpenId] = useState(null);
  const endRef = React.useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    messagingAPI.getHistory({ limit: 1000 })
      .then(r => setLogs(r.data?.data || []))
      .catch(() => toast.error('Failed to load message history'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (thread) endRef.current?.scrollIntoView({ block: 'end' }); }, [thread, logs]);

  const copy = (txt) => navigator.clipboard?.writeText(txt || '').then(() => toast.success('Copied')).catch(() => toast.error('Copy failed'));

  // ── Thread: one customer's messages, oldest first ──
  if (thread) {
    const msgs = logs.filter(l => convKey(l) === thread).reverse();
    const first = msgs[msgs.length - 1] || msgs[0] || {};
    const name = first.customerName || first.mobile || thread;
    return (
      <div>
        <div style={{ position: 'sticky', top: -14, zIndex: 5, background: 'var(--bg)', margin: '-14px -14px 8px', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--border)' }}>
          <button type="button" onClick={() => setThread(null)} aria-label="Back to messages" style={{ width: 44, height: 44, display: 'grid', placeItems: 'center', border: 'none', background: 'none', color: 'var(--text)', cursor: 'pointer' }}><ChevronLeft size={24} /></button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</b>
            {first.mobile && <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>{first.mobile}</span>}
          </div>
          {first.mobile && <a href={`tel:${first.mobile}`} aria-label="Call" className="btn btn-secondary" style={{ width: 44, height: 44, padding: 0, justifyContent: 'center' }}><Phone size={18} /></a>}
        </div>

        {loading && !msgs.length ? <div style={{ textAlign: 'center', padding: 30 }}><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /></div> : msgs.map((l, i) => {
          const open = openId === l._id;
          return (
            <div key={l._id}>
              {(i === 0 || dayLabel(msgs[i - 1].createdAt) !== dayLabel(l.createdAt)) && (
                <div style={{ textAlign: 'center', margin: '12px 0 8px', fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)' }}>{dayLabel(l.createdAt)}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <div onClick={() => setOpenId(open ? null : l._id)}
                  style={{ maxWidth: '86%', padding: '8px 11px', borderRadius: '14px 14px 4px 14px', background: l.status !== 'sent' ? 'var(--cb-fef2f2)' : 'var(--primary-light)', border: l.status !== 'sent' ? '1px solid var(--c-dc2626)' : '1px solid transparent', color: 'var(--text)', cursor: 'pointer' }}>
                  {l.mediaUrls?.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      {l.mediaUrls.map((m, k) => (m.type === 'video'
                        ? <video key={k} src={m.url} controls preload="metadata" style={{ height: 110, borderRadius: 6 }} />
                        : <img key={k} src={m.url} alt="" style={{ height: 110, borderRadius: 6 }} />))}
                    </div>
                  )}
                  <div style={{ fontSize: '.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{l.body}</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginTop: 3, fontSize: '.68rem', color: 'var(--text-muted)' }}>
                    <span>{new Date(l.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    <Ticks l={l} />
                  </div>
                  {l.status !== 'sent' && l.error && <div style={{ fontSize: '.74rem', color: 'var(--c-dc2626)', marginTop: 3 }}>{l.error}</div>}
                  {open && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', fontSize: '.72rem', color: 'var(--text-muted)' }}>
                      {SOURCE_LABELS[l.source] || l.source} · {l.channel}{l.sentByName ? ` · by ${l.sentByName}` : ''}
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => copy(l.body)}>Copy</button>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => onCompose({ body: l.body })}>Reuse text</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
        <div style={{ position: 'sticky', bottom: 0, zIndex: 4, background: 'var(--bg)', margin: '8px -14px -14px', padding: '8px 14px 12px', borderTop: '1px solid var(--border)' }}>
          <button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 48, justifyContent: 'center' }}
            onClick={() => onCompose({ mobile: first.mobile, name })} disabled={!first.mobile}>
            <MessageCircle size={16} style={{ marginRight: 8 }} />Message {name}
          </button>
        </div>
      </div>
    );
  }

  // ── Conversation list: newest first, one row per customer ──
  const convs = [];
  const byKey = {};
  logs.forEach(l => {
    const k = convKey(l);
    if (!byKey[k]) { byKey[k] = { k, last: l, count: 0, failed: 0 }; convs.push(byKey[k]); }
    byKey[k].count += 1;
    if (l.status !== 'sent') byKey[k].failed += 1;
  });
  const q = search.toLowerCase();
  const qd = search.replace(/\D/g, '');
  const shown = convs.filter(c =>
    (!failedOnly || c.failed > 0)
    && (!search || (c.last.customerName || '').toLowerCase().includes(q) || (qd && (c.last.mobile || '').replace(/\D/g, '').includes(qd))));
  const failedConvs = convs.filter(c => c.failed > 0).length;

  return (
    <div>
      <div style={{ position: 'sticky', top: 32, zIndex: 3, background: 'var(--bg)', margin: '0 -14px 4px', padding: '4px 14px 8px' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="search-input-wrapper" style={{ flex: 1, minWidth: 0 }}>
            <Search size={14} className="search-icon" />
            <input className="search-input" placeholder="Search name or mobile" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button type="button" className="btn btn-secondary" onClick={load} aria-label="Refresh" style={{ width: 44, padding: 0, justifyContent: 'center' }}><RefreshCw size={16} /></button>
        </div>
        <div className="mc-chips" style={{ marginTop: 8 }}>
          <button type="button" className={`mc-chip${!failedOnly ? ' on' : ''}`} onClick={() => setFailedOnly(false)}>All</button>
          <button type="button" className={`mc-chip${failedOnly ? ' on' : ''}${failedConvs ? ' bad' : ''}`} onClick={() => setFailedOnly(true)}>Failed{failedConvs ? ` (${failedConvs})` : ''}</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : shown.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>{logs.length === 0 ? 'No messages sent yet' : 'No matches'}</div>
      ) : shown.map(c => {
        const l = c.last;
        const name = l.customerName || l.mobile || '—';
        return (
          <button key={c.k} type="button" onClick={() => setThread(c.k)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 64, textAlign: 'left', padding: '8px 2px', border: 'none', borderBottom: '1px solid var(--border)', background: 'none', color: 'var(--text)', cursor: 'pointer' }}>
            <span style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--primary-light)', color: 'var(--primary)', fontWeight: 800, fontSize: '1.05rem' }}>{name.trim().charAt(0).toUpperCase()}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ flex: 1, minWidth: 0, fontSize: '.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</b>
                <span style={{ fontSize: '.72rem', color: c.failed ? 'var(--c-dc2626)' : 'var(--text-muted)', flexShrink: 0 }}>{shortTime(l.createdAt)}</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{ fontSize: '.78rem', flexShrink: 0 }}><Ticks l={l} /></span>
                <span style={{ flex: 1, minWidth: 0, fontSize: '.82rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.body}</span>
                {c.failed > 0 && <span style={{ minWidth: 20, height: 20, borderRadius: 10, padding: '0 6px', background: 'var(--c-dc2626)', color: '#fff', fontSize: '.7rem', fontWeight: 800, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{c.failed}</span>}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MessageCenterPage() {
  useDarkReady();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('send');
  const [templates, setTemplates] = useState([]);
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  // App View: the tab row is always visible. `thread` = open customer conversation,
  // `compose` = the write screen ({} blank, or a seed: template / reused text / one mobile).
  const [appTab, setAppTab] = useState('messages');
  const [thread, setThread] = useState(null);
  const [compose, setCompose] = useState(null);
  const sendTemplate = (t) => setCompose({ body: t.body, templateId: t._id, extras: { imageUrl: t.imageUrl || '', footer: t.footer || '', buttons: t.buttons || [] } });

  const loadTemplates = useCallback(() => {
    messagingAPI.getTemplates().then(r => setTemplates(r.data?.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  return (
    <APP.Provider value={isApp}>
    <div>
      {!isApp && (
        <div className="page-header">
          <div>
            <h1 className="page-title">Message Center</h1>
            <p className="page-subtitle">Customer WhatsApp broadcasts and reminders, plus the team alerts for network faults, complaints and payments</p>
          </div>
        </div>
      )}

      {isApp ? (
        <>
          {compose ? (
            <>
              <div style={{ position: 'sticky', top: -14, zIndex: 5, background: 'var(--bg)', margin: '-14px -14px 10px', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--border)' }}>
                <button type="button" onClick={() => setCompose(null)} aria-label="Cancel"
                  style={{ width: 44, height: 44, display: 'grid', placeItems: 'center', border: 'none', background: 'none', color: 'var(--text)', cursor: 'pointer' }}><ChevronLeft size={24} /></button>
                <b style={{ flex: 1, fontSize: '1rem' }}>{compose.name ? `Message ${compose.name}` : 'New message'}</b>
              </div>
              <SendTab key={compose.templateId || compose.mobile || compose.body || 'blank'} templates={templates} seed={compose} />
            </>
          ) : (
            <>
              {!thread && (
                <div style={{ position: 'sticky', top: -14, zIndex: 5, background: 'var(--bg)', margin: '-14px -14px 6px', display: 'flex', borderBottom: '1px solid var(--border)' }}>
                  {[['messages', 'Messages'], ['templates', 'Templates'], ['notify', 'Reminders'], ['alerts', 'Alerts'], ['push', 'Push']].map(([k, lbl]) => (
                    <button key={k} type="button" onClick={() => setAppTab(k)}
                      style={{ flex: 1, minHeight: 46, padding: '0 4px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '.8rem', fontWeight: appTab === k ? 800 : 600, color: appTab === k ? 'var(--primary)' : 'var(--text-muted)', borderBottom: appTab === k ? '3px solid var(--primary)' : '3px solid transparent' }}>{lbl}</button>
                  ))}
                </div>
              )}
              {appTab === 'messages' && <MessagesApp thread={thread} setThread={setThread} onCompose={setCompose} />}
              {appTab === 'push' && <PushOffersTab />}
              {appTab === 'templates' && <TemplatesTab templates={templates} reload={loadTemplates} isAdmin={isAdmin} onUse={sendTemplate} />}
              {appTab === 'notify' && <CustomerNotifyTab isAdmin={isAdmin} goToTemplates={() => setAppTab('templates')} />}
              {appTab === 'alerts' && <AlertsTab isAdmin={isAdmin} goToTemplates={() => setAppTab('templates')} />}
              {appTab === 'messages' && !thread && (
                <div style={{ position: 'sticky', bottom: 14, height: 0, display: 'flex', justifyContent: 'flex-end', zIndex: 4, pointerEvents: 'none' }}>
                  <button type="button" onClick={() => setCompose({})} aria-label="New message"
                    style={{ pointerEvents: 'auto', transform: 'translateY(-100%)', height: 52, padding: '0 20px 0 16px', borderRadius: 26, border: 'none', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: '.9rem', cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,.28)' }}>
                    <Plus size={22} /> New
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            {[
              { key: 'send', label: 'Send Message', icon: Send },
              { key: 'push', label: 'Push Offers', icon: Megaphone },
              { key: 'templates', label: 'Templates', icon: FileText },
              { key: 'notify', label: 'Customer Notify', icon: BellRing },
              { key: 'alerts', label: 'Alerts', icon: Siren },
              { key: 'history', label: 'History', icon: History },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-secondary'}`}>
                <t.icon size={14} style={{ marginRight: 6 }} />{t.label}
              </button>
            ))}
          </div>

          {tab === 'send' && <SendTab templates={templates} />}
          {tab === 'push' && <PushOffersTab />}
          {tab === 'templates' && <TemplatesTab templates={templates} reload={loadTemplates} isAdmin={isAdmin} />}
          {tab === 'notify' && <CustomerNotifyTab isAdmin={isAdmin} goToTemplates={() => setTab('templates')} />}
          {tab === 'alerts' && <AlertsTab isAdmin={isAdmin} goToTemplates={() => setTab('templates')} />}
          {tab === 'history' && <HistoryTab />}
        </>
      )}
    </div>
    </APP.Provider>
  );
}
